import React, { FC, useEffect, useMemo, useState } from "react"
import { observer } from "mobx-react"
import { Button, Modal, Table, Input, Form, Select, Tabs, Tag, Descriptions, Empty } from "antd"
import { notifications } from "notifications"
import { useStore } from "stores/utils/use-store"
import { EditIconButton } from "components"
import Styles from "./styles.module.css"
import { EditGameInfoModal } from "./edit-game-info-modal"
import { BonusMissionsModal } from "./bonus-missions-modal"
import { DailyMissionsModal } from "./daily-missions-modal"
import { GamePlatformType, GameTask, NativeGame, GameTaskType } from "../store/types"
import { nativeGameDetailsColumns } from "./columns"
import { gameTaskTypeOptions } from "../constants"
import { PlatformTabLabel } from "./platform-tab-label"

type Props = {
  selectedGame: NativeGame;
  setSelectedGame: React.Dispatch<React.SetStateAction<NativeGame | null>>;
};

const GLOBAL_TAB_KEY = "__global__"

/**
 * Details view for a single native game. Right panel is a Tabs strip with:
 * - "Global" tab: read-only summary of cross-platform fields + Edit Game
 * - One tab per platform: per-platform config readout, per-platform
 *   tasks table (only the tasks for that platform), and a Pause/Activate
 *   action that flips is_installable for just this platform.
 *
 * Per-platform pause means an admin can take Android offline while iOS keeps
 * earning, matching the server-side budget-limit-reached semantics.
 */
export const NativeGameDetailsModal: FC<Props> = observer(({ selectedGame, setSelectedGame }) => {
  const [editTask, setEditTask] = useState<GameTask | null>(null)
  const [editGame, setEditGame] = useState<NativeGame | null>(null)
  const [addTaskPlatform, setAddTaskPlatform] = useState<GamePlatformType | null>(null)
  const [isBonusMissionsOpen, setIsBonusMissionsOpen] = useState(false)
  const [isDailyMissionsOpen, setIsDailyMissionsOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<string>(GLOBAL_TAB_KEY)
  const { dataStore: { nativeGamesControlStore } } = useStore()
  const [form] = Form.useForm()

  const tasksByPlatform = useMemo(() => {
    const map = new Map<GamePlatformType, GameTask[]>()
    for (const task of selectedGame.tasks || []) {
      const list = map.get(task.platform) ?? []
      list.push(task)
      map.set(task.platform, list)
    }
    map.forEach(list => list.sort((a, b) => a.sequence - b.sequence))

    return map
  }, [selectedGame.tasks])

  useEffect(() => {
    if (editTask) {
      form.setFieldsValue(editTask)
    }
  }, [editTask, form])

  useEffect(() => {
    if (addTaskPlatform) {
      form.setFieldsValue({ platform: addTaskPlatform })
    }
  }, [addTaskPlatform, form])

  const onFinishHandler = async(values: any) => {
    try {
      const parsedValues = {
        ...values,
        // rewardUsd is required end-to-end (form rule + admin-api DTO + buff-play-service DTO),
        // so by the time onFinish fires it's guaranteed to be set.
        rewardUsd: Number(values.rewardUsd),
        bonusRewardUsd:
          values.bonusRewardUsd != null && values.bonusRewardUsd !== "" ? Number(values.bonusRewardUsd) : null,
        sequence: Number(values.sequence),
        appsflyerRevenue:
          values.appsflyerRevenue != null && values.appsflyerRevenue !== "" ? Number(values.appsflyerRevenue) : null,
        cpeRevenue: values.cpeRevenue != null && values.cpeRevenue !== "" ? Number(values.cpeRevenue) : null,
        expiresInMinutes: Number(values.expiresInMinutes) || null,
        iconNumber: Number(values.iconNumber),
        adsWatchedTotalRequired:
          values.type === GameTaskType.WATCH_AN_ADS_TASK ? Number(values.adsWatchedTotalRequired) : null,
        taskToken:
          values.type === GameTaskType.BONUS_TASK && (values.taskToken == null || String(values.taskToken).trim() === "")
            ? undefined
            : values.taskToken,
      }
      if (editTask) {
        await nativeGamesControlStore.editNativeGameTask({
          gameId: selectedGame.id,
          taskId: editTask.id,
          ...parsedValues,
        })
        setSelectedGame(prev => {
          if (!prev) return prev

          const updatedTasks = prev.tasks
            .map(task => task.id === editTask.id ? { ...task, ...parsedValues } : task)
            .sort((a, b) => a.sequence - b.sequence)

          return { ...prev, tasks: updatedTasks }
        })
        notifications.success("Task updated successfully")
        setEditTask(null)
        form.resetFields()
      } else {
        const newTask = await nativeGamesControlStore.createNativeGameTask(selectedGame.id, { ...parsedValues })
        setSelectedGame(prev => {
          if (!prev) return prev

          return {
            ...prev,
            tasks: [...prev.tasks, newTask].sort((a, b) => a.sequence - b.sequence),
          }
        })
        notifications.success("Task created successfully")
        setAddTaskPlatform(null)
        form.resetFields()
      }
    } catch (err: any) {
      notifications.error(err?.message || "Failed to proceed")
    }
  }

  const togglePlatformInstallable = (platform: GamePlatformType, nextValue: boolean) => {
    Modal.confirm({
      title: nextValue ? "Confirm Activation" : "Confirm Deactivation",
      content: nextValue
        ? `Activate ${platform}? Users on this platform will see the game again.`
        : `Pause ${platform}? Users on this platform will stop seeing the game.`,
      okText: nextValue ? "Yes, activate" : "Yes, pause",
      okType: nextValue ? "primary" : "primary",
      okButtonProps: nextValue ? undefined : { className: Styles.pauseButton },
      cancelText: "Cancel",
      onOk: async() => {
        try {
          await nativeGamesControlStore.setPlatformInstallableStatus(selectedGame.id, platform, nextValue)
          notifications.success(`${platform} ${nextValue ? "activated" : "paused"}`)
          setSelectedGame(prev => {
            if (!prev) return prev

            return {
              ...prev,
              platforms: prev.platforms.map(p => p.platform === platform ? { ...p, isInstallable: nextValue } : p,
              ),
            }
          })
        } catch (err: any) {
          notifications.error(err?.message || "Failed to update platform")
        }
      },
    })
  }

  const confirmDeletionGame = () => {
    Modal.confirm({
      title: "Confirm Removal",
      content: "Are you sure you want to delete this game?",
      okText: "Yes, Delete Game",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async() => {
        try {
          await nativeGamesControlStore.deleteNativeGame(selectedGame.id)
          notifications.success("Game successfully deleted")
          setSelectedGame(prev => {
            if (!prev) return prev

            return { ...prev, isDeleted: new Date() }
          })
        } catch (err: any) {
          notifications.error(err?.message || "Failed to delete game")
        }
      },
    })
  }

  const confirmRestoreGame = async() => {
    Modal.confirm({
      title: "Confirm Recovery",
      content: "Do you want to restore this game?",
      okText: "Yes, Restore Game",
      okType: "primary",
      cancelText: "Cancel",
      onOk: async() => {
        try {
          await nativeGamesControlStore.restoreNativeGame(selectedGame.id)
          notifications.success("Game successfully restored")
          setSelectedGame(prev => {
            if (!prev) return prev

            return { ...prev, isDeleted: null }
          })
        } catch (err: any) {
          notifications.error(err?.message || "Failed to restore game")
        }
      },
    })
  }

  const columns = selectedGame.isDeleted
    ? [...nativeGameDetailsColumns]
    : [
      ...nativeGameDetailsColumns,
      {
        title: "Update Task",
        key: "update",
        render: (_: any, row: GameTask) => <EditIconButton onClick={() => setEditTask(row)} />,
      },
    ]

  return (
    <Modal
      open
      onCancel={() => {
        nativeGamesControlStore.nativeGames = nativeGamesControlStore.nativeGames
          .map(game => (game.id === selectedGame.id ? selectedGame : game))
        setSelectedGame(null)
      }}
      width="100%"
      wrapClassName={Styles.modalWrapperNoScroll}
      bodyStyle={{ height: "100vh", padding: 0 }}
      className={Styles.fullscreenModal}
      footer={null}
      title="Update Game"
    >
      <div style={{ display: "flex" }}>
        <div className={Styles.leftPanel}>
          <img src={selectedGame?.bannerUrl} style={{ marginBottom: "10px" }} alt="" />
          <div style={{ marginBottom: "10px" }}>Name: {selectedGame?.name}</div>
          <div style={{ marginBottom: "10px" }}>Category: {selectedGame?.category}</div>
          <div style={{ marginBottom: "10px", display: "flex", gap: 4, flexWrap: "wrap" }}>
            {(selectedGame.platforms ?? []).map(p => (
              <Tag key={p.platform} color={p.isInstallable ? "green" : "default"}>
                {p.platform}: {p.isInstallable ? "Active" : "Paused"}
              </Tag>
            ))}
          </div>
          <Button
            type="primary"
            style={{ marginBottom: "10px" }}
            onClick={() => setEditGame(selectedGame)}
            disabled={!!selectedGame.isDeleted}
          >
            Edit Game (Global + platforms)
          </Button>
          <Button
            type="primary"
            style={{ marginBottom: "10px" }}
            onClick={() => setIsBonusMissionsOpen(true)}
            disabled={!!selectedGame.isDeleted}
          >
            Configure Bonus Missions
          </Button>
          <Button
            type="primary"
            style={{ marginBottom: "10px" }}
            onClick={() => setIsDailyMissionsOpen(true)}
            disabled={!!selectedGame.isDeleted}
          >
            Configure Daily Missions
          </Button>
          <Button
            className={selectedGame.isDeleted ? Styles.restoreButton : undefined}
            style={{ marginBottom: "10px" }}
            danger={!selectedGame.isDeleted}
            onClick={() => (selectedGame.isDeleted ? confirmRestoreGame() : confirmDeletionGame())}
          >
            {selectedGame.isDeleted ? "Restore Game" : "Delete Game"}
          </Button>
        </div>
        <div className={Styles.rightPanel}>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: GLOBAL_TAB_KEY,
                label: "Global",
                children: (
                  <Descriptions bordered column={1} size="small" style={{ margin: 16 }}>
                    <Descriptions.Item label="Name">{selectedGame.name}</Descriptions.Item>
                    <Descriptions.Item label="Category">{selectedGame.category}</Descriptions.Item>
                    <Descriptions.Item label="Description">{selectedGame.description || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Earning Speed">{selectedGame.earningSpeed}</Descriptions.Item>
                    <Descriptions.Item label="Top Pick">{selectedGame.isTopPick ? "Yes" : "No"}</Descriptions.Item>
                    <Descriptions.Item label="Engaging users only">
                      {selectedGame.isOnlyForEngagingAccount ? "Yes" : "No"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Banner">
                      <a href={selectedGame.bannerUrl} target="_blank" rel="noreferrer">{selectedGame.bannerUrl}</a>
                    </Descriptions.Item>
                    {selectedGame.videoBannerUrl && (
                      <Descriptions.Item label="Video banner">
                        <a href={selectedGame.videoBannerUrl} target="_blank" rel="noreferrer">
                          {selectedGame.videoBannerUrl}
                        </a>
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                ),
              },
              ...(selectedGame.platforms || []).map(p => ({
                key: p.platform,
                label: (
                  <PlatformTabLabel
                    platform={p.platform}
                    isInstallable={p.isInstallable}
                    isBudgetExhausted={!!p.dailyLimitReachedAt || !!p.monthlyLimitReachedAt}
                  />
                ),
                children: (
                  <div style={{ padding: 16 }}>
                    <Descriptions bordered column={2} size="small" style={{ marginBottom: 16 }}>
                      <Descriptions.Item label="Platform ID">{p.platformId}</Descriptions.Item>
                      <Descriptions.Item label="Rating">{p.rating}</Descriptions.Item>
                      <Descriptions.Item label="Install Revenue">{p.installRevenue ?? "-"}</Descriptions.Item>
                      <Descriptions.Item label="AppsFlyer Revenue">{p.appsflyerRevenue ?? "-"}</Descriptions.Item>
                      <Descriptions.Item label="Attribution Platform">{p.attributionPlatform}</Descriptions.Item>
                      <Descriptions.Item label="Attribution Window">
                        {p.attributionWindowDays ?? "unlimited"}
                      </Descriptions.Item>
                      <Descriptions.Item label="CTA Link" span={2}>
                        <a href={p.ctaLink} target="_blank" rel="noreferrer">{p.ctaLink}</a>
                      </Descriptions.Item>
                      <Descriptions.Item label="CTA Campaign" span={2}>{p.ctaCampaign}</Descriptions.Item>
                      <Descriptions.Item label="Publisher" span={2}>{p.gamePublisher?.name ?? "-"}</Descriptions.Item>
                      <Descriptions.Item label="Daily Budget">
                        {p.isBudgetActive ? `$${p.dailyBudget ?? 0}` : "off"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Monthly Budget">
                        {p.isBudgetActive ? `$${p.monthlyBudget ?? 0}` : "off"}
                      </Descriptions.Item>
                      {p.dailyLimitReachedAt && (
                        <Descriptions.Item label="Daily limit reached at" span={2}>
                          <Tag color="red">{new Date(p.dailyLimitReachedAt).toLocaleString()}</Tag>
                        </Descriptions.Item>
                      )}
                      {p.monthlyLimitReachedAt && (
                        <Descriptions.Item label="Monthly limit reached at" span={2}>
                          <Tag color="red">{new Date(p.monthlyLimitReachedAt).toLocaleString()}</Tag>
                        </Descriptions.Item>
                      )}
                    </Descriptions>

                    <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                      <Button
                        type="primary"
                        onClick={() => setAddTaskPlatform(p.platform)}
                        disabled={!!selectedGame.isDeleted}
                      >
                        Add Task for {p.platform}
                      </Button>
                      <Button
                        className={p.isInstallable ? Styles.pauseButton : Styles.activateButton}
                        onClick={() => togglePlatformInstallable(p.platform, !p.isInstallable)}
                        disabled={!!selectedGame.isDeleted}
                      >
                        {p.isInstallable ? `Pause ${p.platform}` : `Activate ${p.platform}`}
                      </Button>
                    </div>

                    <Table
                      dataSource={tasksByPlatform.get(p.platform) ?? []}
                      columns={columns}
                      rowKey="id"
                      pagination={false}
                      loading={nativeGamesControlStore.isLoading}
                      locale={{ emptyText: <Empty description={`No tasks for ${p.platform}`} /> }}
                    />
                  </div>
                ),
              })),
            ]}
          />
        </div>
      </div>

      <Modal
        open={!!editTask || !!addTaskPlatform}
        title={editTask ? "Edit Task" : `Add New Task (${addTaskPlatform})`}
        onCancel={() => {
          form.resetFields()
          setEditTask(null)
          setAddTaskPlatform(null)
        }}
        footer={null}
      >
        <Form layout="vertical" form={form} onFinish={onFinishHandler}>
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: "Task name is required" }]}
          >
            <Input placeholder="Please enter task name" />
          </Form.Item>
          {!editTask && (
            <Form.Item
              label="Platform"
              name="platform"
              rules={[{ required: true, message: "Platform is required" }]}
              tooltip="Pre-filled from the platform tab you opened this dialog from."
            >
              <Select
                placeholder="Please select Platform"
                options={(selectedGame.platforms || []).map(p => ({ value: p.platform, label: p.platform }))}
                disabled
              />
            </Form.Item>
          )}
          <Form.Item
            label="Sequence"
            name="sequence"
            rules={[{ required: true, message: "Sequence is required" }]}
          >
            <Input placeholder="Please enter sequence" type="number" min={1} />
          </Form.Item>
          <Form.Item
            label="Reward ($)"
            name="rewardUsd"
            rules={[{ required: true, message: "Reward is required" }]}
          >
            <Input placeholder="USD" type="number" min={0} step={0.01} />
          </Form.Item>
          <Form.Item
            label="Type"
            name="type"
            rules={[{ required: true, message: "Task type is required" }]}
          >
            <Select options={gameTaskTypeOptions} placeholder="Please select task type" />
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, curValues) => prevValues.type !== curValues.type}
          >
            {({ getFieldValue }) => getFieldValue("type") === GameTaskType.WATCH_AN_ADS_TASK ? (
              <Form.Item
                label="Ads Watched Total Required"
                name="adsWatchedTotalRequired"
                rules={[
                  { required: true, message: "Ads watched total required is required for Watch an Ads task" },
                  {
                    validator: (_, value) => !value || Number(value) >= 1
                      ? Promise.resolve()
                      : Promise.reject(new Error("Must be at least 1")),
                  },
                ]}
              >
                <Input type="number" min={1} placeholder="e.g. 10" />
              </Form.Item>
            ) : null}
          </Form.Item>
          <Form.Item label="AppsFlyer Revenue" name="appsflyerRevenue">
            <Input type="number" min={0} step={0.01} placeholder="Please enter AppsFlyer Rev" />
          </Form.Item>
          <Form.Item label="CPE Revenue" name="cpeRevenue">
            <Input type="number" min={0} step={0.01} placeholder="Please enter CPE Revenue" />
          </Form.Item>
          <Form.Item label="Bonus Reward ($)" name="bonusRewardUsd">
            <Input type="number" min={0} step={0.01} placeholder="USD" />
          </Form.Item>
          <Form.Item label="Expires In Minutes" name="expiresInMinutes">
            <Input type="number" min={1} placeholder="Please enter Expires (min)" />
          </Form.Item>
          <Form.Item
            label="Icon Number"
            name="iconNumber"
            rules={[{ required: true, message: "Icon number is required" }]}
          >
            <Input type="number" min={1} placeholder="Please enter Icon number" />
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, curValues) => prevValues.type !== curValues.type}
          >
            {({ getFieldValue }) => {
              const isBonus = getFieldValue("type") === GameTaskType.BONUS_TASK

              return (
                <Form.Item
                  label="Task Token"
                  name="taskToken"
                  rules={isBonus ? [] : [{ required: true, message: "Task Token is required" }]}
                >
                  <Input placeholder={isBonus ? "Optional for BONUS_TASK" : "e.g. reached_level_20"} />
                </Form.Item>
              )
            }}
          </Form.Item>
          <Form.Item>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <Button
                onClick={() => {
                  form.resetFields()
                  setEditTask(null)
                  setAddTaskPlatform(null)
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Save
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      <EditGameInfoModal
        editGame={editGame}
        setEditGame={setEditGame}
        setSelectedGame={setSelectedGame}
      />

      {isBonusMissionsOpen && (
        <BonusMissionsModal
          game={selectedGame}
          onClose={() => setIsBonusMissionsOpen(false)}
        />
      )}

      {isDailyMissionsOpen && (
        <DailyMissionsModal
          game={selectedGame}
          onClose={() => setIsDailyMissionsOpen(false)}
        />
      )}
    </Modal>
  )
})
